import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Gift, TrendingUp, ArrowDownCircle } from "lucide-react";
import { useSettings } from "../../context/SettingsContext";
const RewardsTab = () => {
  const { token } = useSelector((state) => state.auth);
  const [rewardsData, setRewardsData] = useState(null);
  const [loading, setLoading] = useState(false);
const { settings } = useSettings();
  useEffect(() => {
    const fetchRewards = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${window.BASE_API_URL}myrewards/${token}`
        );
        if (res.data.success) {
          setRewardsData(res.data.data);
        } else {
          toast.error("Failed to fetch rewards data");
        }
      } catch (err) {
        console.error(err);
        toast.error("Error fetching rewards");
      } finally {
        setLoading(false);
      }
    };
    fetchRewards();
  }, [token]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40 text-gray-500">
        Loading rewards...
      </div>
    );
  }

  if (!rewardsData) {
    return (
      <div className="text-center py-10 text-gray-500">
        No rewards data available.
      </div>
    );
  }

  const totalEarned = rewardsData.rewards_sum || 0;
  const totalRedeemed = rewardsData.reedem_sum || 0;
  const balance = totalEarned - totalRedeemed;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <h2 className="text-2xl font-semibold text-amber-700 mb-4"  style={{ color: settings?.cart_btn_color || "#111827" }}>
        My Rewards
      </h2>
      <p className="text-gray-600 mb-8">
        Track your earned points and redeemed rewards below.
      </p>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border border-amber-100 shadow-sm rounded-xl p-5 text-center">
          <TrendingUp className="mx-auto w-8 h-8 text-amber-600 mb-2" />
          <p className="text-gray-600 text-sm">Total Earned</p>
          <p className="text-xl font-bold text-gray-800">
            {totalEarned.toFixed(2)}
          </p>
        </div>

        <div className="bg-white border border-amber-100 shadow-sm rounded-xl p-5 text-center">
          <ArrowDownCircle className="mx-auto w-8 h-8 text-red-500 mb-2" />
          <p className="text-gray-600 text-sm">Redeemed</p>
          <p className="text-xl font-bold text-gray-800">
            {totalRedeemed.toFixed(2)}
          </p>
        </div>

        <div className="bg-white border border-amber-100 shadow-sm rounded-xl p-5 text-center">
          <Gift className="mx-auto w-8 h-8 text-green-600 mb-2" />
          <p className="text-gray-600 text-sm">Available Balance</p>
          <p className="text-xl font-bold text-gray-800">
            {balance.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Earned Rewards Section */}
      <div className="mb-10">
        <h3 className="text-lg font-semibold text-gray-800 mb-3"  style={{ color: settings?.cart_title_color || "#111827" }}>
          🏆 Earned Rewards
        </h3>
        {rewardsData.rewards && rewardsData.rewards.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-xl shadow border border-amber-100">
              <thead>
                <tr className="bg-amber-50 text-amber-800 text-sm uppercase">
                  <th className="py-3 px-4 text-left">Order ID</th>
                  <th className="py-3 px-4 text-left">Description</th>
                  <th className="py-3 px-4 text-right">Points</th>
                  <th className="py-3 px-4 text-right">Date</th>
                </tr>
              </thead>
              <tbody>
                {rewardsData.rewards.map((reward) => (
                  <tr
                    key={reward.id}
                    className="border-t border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-3 px-4">{reward.order_id + 1000}</td>
                    <td className="py-3 px-4">{reward.description}</td>
                    <td className="py-3 px-4 text-right font-semibold text-amber-700">
                      +{Number(reward.rewards).toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-600">
                      {reward.created_at}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-sm mt-2">No earned rewards yet.</p>
        )}
      </div>

      {/* Redeemed Rewards Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-3"  style={{ color: settings?.cart_title_color || "#111827" }}>
          💸 Redeemed Rewards
        </h3>
        {rewardsData.reedem && rewardsData.reedem.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-xl shadow border border-amber-100">
              <thead>
                <tr className="bg-amber-50 text-amber-800 text-sm uppercase">
                  <th className="py-3 px-4 text-left">Order No</th>
                  <th className="py-3 px-4 text-left">Payment Type</th>
                  <th className="py-3 px-4 text-right">Redeemed Points</th>
                  <th className="py-3 px-4 text-right">Date</th>
                </tr>
              </thead>
              <tbody>
                {rewardsData.reedem.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-3 px-4">{r.order_no}</td>
                    <td className="py-3 px-4">{r.payment_method}</td>
                    <td className="py-3 px-4 text-right font-semibold text-red-500">
                      -{r.rewards_amount ?? r.rewards_point ?? 0}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-600">
                      {r.order_date} {r.order_time}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-sm mt-2">No redeemed rewards yet.</p>
        )}
      </div>
    </div>
  );
};

export default RewardsTab;
